/**
 * Downstream peak discharge: this model against the modelling team's HEC-RAS run.
 *
 * Two independent models on the same reach, on the same axes. That is step 7 of
 * the project flowchart and the strongest single thing here to put in front of
 * an evaluator - which is exactly why it has to be honest about its own
 * measurement. Points whose volume check fails are still plotted and still
 * listed; they are marked, and they are kept out of the headline average.
 */

import { useQuery } from '@tanstack/react-query'
import {
  CartesianGrid,
  Legend as ChartLegend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { api, ApiError } from '../api/client'
import { C, fs } from '../design/tokens'

function formatCumecs(value: number | null | undefined): string {
  if (value == null) return '-'
  return `${Math.round(value).toLocaleString()}`
}

export function AttenuationPanel({ runId }: { runId: string }) {
  const query = useQuery({
    queryKey: ['attenuation', runId],
    queryFn: () => api.attenuation(runId),
    retry: false,
  })

  if (query.isPending) {
    return (
      <section className="rounded-md border border-slate-800 bg-slate-900 p-3">
        <h3 className="text-xs font-medium uppercase tracking-wider text-slate-400">
          Downstream comparison
        </h3>
        <p className="mt-2 text-xs text-slate-500">
          Cutting cross-sections along the reach&hellip;
        </p>
      </section>
    )
  }

  if (query.isError) {
    const message =
      query.error instanceof ApiError ? query.error.message : 'Comparison failed'
    return (
      <section className="rounded-md border border-slate-800 bg-slate-900 p-3">
        <h3 className="text-xs font-medium uppercase tracking-wider text-slate-400">
          Downstream comparison
        </h3>
        <p className="mt-2 text-12 leading-snug text-amber-400">{message}</p>
      </section>
    )
  }

  const data = query.data
  const chart = data.points
    .filter((point) => point.our_peak_cumecs != null)
    .map((point) => ({
      chainage: point.chainage_km,
      theirs: point.their_peak_cumecs,
      ours: point.our_peak_cumecs,
    }))

  return (
    <section className="rounded-md border border-slate-800 bg-slate-900 p-3">
      <h3 className="text-xs font-medium uppercase tracking-wider text-slate-400">
        Downstream comparison
      </h3>
      <p className="text-12 text-slate-500">
        Peak discharge against their HEC-RAS run, scenario{' '}
        <span className="text-slate-400">{data.scenario_key}</span>
      </p>

      <div className="mt-2 h-40">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chart} margin={{ top: 6, right: 8, bottom: 0, left: -8 }}>
            <CartesianGrid stroke={C.grid} />
            <XAxis
              dataKey="chainage"
              type="number"
              tick={{ fontSize: fs(12), fill: C.textFaint }}
              stroke={C.grid}
              tickFormatter={(value: number) => `${value}km`}
            />
            <YAxis
              tick={{ fontSize: fs(12), fill: C.textFaint }}
              stroke={C.grid}
              width={48}
              tickFormatter={(value: number) => `${Math.round(value / 1000)}k`}
            />
            <Tooltip
              contentStyle={{
                background: C.bg,
                border: '1px solid var(--ink-grid)',
                fontSize: fs(12),
              }}
              labelFormatter={(value: number) => `${value} km along the Bhima`}
              formatter={(value: number, name: string) => [
                `${formatCumecs(value)} m³/s`,
                name,
              ]}
            />
            <ChartLegend wrapperStyle={{ fontSize: fs(12) }} />
            <Line
              type="monotone"
              dataKey="theirs"
              name="HEC-RAS (team)"
              stroke={C.warn}
              strokeWidth={1.5}
              dot={{ r: 2.5 }}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="ours"
              name="QuickFlood"
              stroke={C.accent}
              strokeWidth={1.5}
              dot={{ r: 2.5 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <table className="mt-2 w-full text-left text-12">
        <thead className="text-12 uppercase tracking-wider text-slate-500">
          <tr>
            <th className="py-1 pr-2 font-medium">Chainage</th>
            <th className="py-1 pr-2 text-right font-medium">Theirs</th>
            <th className="py-1 pr-2 text-right font-medium">Ours</th>
            <th className="py-1 text-right font-medium">Diff</th>
          </tr>
        </thead>
        <tbody className="text-slate-300">
          {data.points.filter((point) => !point.outside_modelled_domain).map((point) => (
            <tr key={point.chainage_km} className="border-t border-slate-800">
              <td className="py-1 pr-2">
                {point.chainage_km} km
              </td>
              <td className="py-1 pr-2 text-right tabular-nums">
                {formatCumecs(point.their_peak_cumecs)}
              </td>
              <td className="py-1 pr-2 text-right tabular-nums">
                {point.beyond_reach ? (
                  <span className="text-slate-500">beyond reach</span>
                ) : (
                  formatCumecs(point.our_peak_cumecs)
                )}
              </td>
              <td
                className={[
                  'py-1 text-right tabular-nums',
                  point.difference_pct == null
                    ? 'text-slate-500'
                    : Math.abs(point.difference_pct) < 25
                      ? 'text-emerald-400'
                      : 'text-amber-400',
                ].join(' ')}
              >
                {point.difference_pct == null
                    ? '-'
                    : `${point.difference_pct > 0 ? '+' : ''}${point.difference_pct.toFixed(0)}%`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="mt-2 text-12 leading-snug text-slate-400">
        {data.mean_absolute_difference_pct == null ? (
          'No point passed the volume check, so no average is quoted.'
        ) : (
          <>
            Mean absolute difference{' '}
            <span className="tabular-nums text-slate-200">
              {data.mean_absolute_difference_pct.toFixed(0)}%
            </span>{' '}
            over {data.points_trusted} of {data.points_compared} points.
          </>
        )}
      </p>
    </section>
  )
}
